import React, { useEffect } from 'react';
import { useQuotaPanelState } from '../hooks/useQuotaPanelState';

const WINDOW_LABELS = {
  month: 'le mois en cours',
  isoQuarter: 'le trimestre en cours',
  rollingDays: (days) => `les ${days} derniers jours`,
  none: 'la période',
  fixed: 'la période définie',
};

function buildExceededMessage({ limit, filters, window }) {
  const parts = [`Quota atteint : max ${limit} demande${limit > 1 ? 's' : ''}`];

  const customFields = filters?.customFields || [];
  const fieldParts = customFields
    .map((f) => f.value);

  if (fieldParts.length > 1) {
    parts.push(fieldParts.join(' / '));
  }

  const windowType = window?.type || 'none';
  const windowLabel = typeof WINDOW_LABELS[windowType] === 'function'
    ? WINDOW_LABELS[windowType](window?.days)
    : WINDOW_LABELS[windowType];

  parts.push(`sur ${windowLabel}.`);

  return parts.join(' ');
}

const ProgressBar = ({ value, max }) => {
  const safeMax = Math.max(1, Number(max) || 1);
  const safeValue = Math.min(safeMax, Math.max(0, Number(value) || 0));
  return (
    <progress
      value={safeValue}
      max={safeMax}
      className="w-full h-2 overflow-hidden rounded bg-gray-200 [&::-webkit-progress-bar]:bg-gray-200 [&::-webkit-progress-value]:bg-primary [&::-moz-progress-bar]:bg-primary"
    />
  );
};

export default function QuotaPanel({
  enabled,
  serviceDeskId,
  requestTypeId,
  requiredFieldIds = [],
  formData,
  onQuotaStateChange = () => {},
}) {
  const {
    hasQuota,
    countLoading,
    checkLoading,
    checkData,
    checkError,
    checking,
    blocking,
  } = useQuotaPanelState({
    enabled,
    serviceDeskId,
    requestTypeId,
    requiredFieldIds,
    formData,
  });

  // Expose state to parent (disable submit, etc.)
  useEffect(() => {
    onQuotaStateChange({ checking, blocking });
  }, [checking, blocking, onQuotaStateChange]);

  if (!enabled) return null;
  if (!hasQuota && !countLoading && !checkError) return null;
  if (!hasQuota && countLoading) return null; // keep it transparent until we know

  // Panel shown whenever requestType has quotas configured.
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-4">
      <div className="text-sm font-semibold text-gray-900">Quota</div>

      {!checkData && (
        <div className="mt-1 text-sm text-gray-700">
          Ce type de demande est soumis à une gestion de quota. Le quota sera calculé lorsque tous les champs obligatoires seront remplis.
        </div>
      )}

      {checkLoading && (
        <div className="mt-3 text-sm text-gray-600">Vérification du quota…</div>
      )}

      {!checkLoading && checkError && (
        <div className="mt-3 text-sm text-red-700">
          Échec de la vérification du quota.
        </div>
      )}

      {!checkLoading && checkData && checkData.status === 'no_quota_found' && (
        <div className="mt-3 text-sm text-gray-700">
          Aucun quota ne s'applique à la sélection actuelle.
        </div>
      )}

      {!checkLoading && checkData && Array.isArray(checkData.rules) && checkData.rules.length > 0 && (
        <div className="mt-4 space-y-3">
          {checkData.rules.map(({ id, limit, count, exceeded, filters, window }) => {
            return (
              <div key={id} className="rounded border border-gray-100 bg-gray-50 px-3 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-medium text-gray-900">&nbsp;</div>
                  <div className={`text-sm ${exceeded ? 'text-red-700' : 'text-gray-700'}`}>
                    {count} / {limit}
                  </div>
                </div>

                <div className="mt-2">
                  <ProgressBar value={Math.min(count, limit)} max={Math.max(1, limit)} />
                </div>

                {exceeded ? (
                  <div className="mt-2 text-xs">
                    <p className="text-red-700">{buildExceededMessage({ limit, filters, window })}</p>
                    <p className="text-amber-700">Votre demande sera créée en brouillon afin de vous permettre de la soumettre lorsque les quotas seront réinitialisés sur la période suivante.</p>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm text-gray-700">Quota maximum non atteint, vous pouvez soumettre une nouvelle demande.</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


